import { useState, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import BuildingIcon from "lucide-react/dist/esm/icons/building.js";
import { ArchiveRestore, MoreHorizontal, Pencil, Save, Trash2, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PermissionAwareButton } from "@/components/ui/permission-aware-button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { formatBuildingAddress } from "@/utils/formatBuildingAddress";
import type { OrgProjectOverview } from "@/stores/organizations/repository";
import { PROJECT_TILE_SURFACE_CLASS } from "@/domains/projects/components/projectTile";
import { cn } from "@/lib/utils";
import { PortfolioProjectRows } from "./PortfolioProjectRows";

export interface PortfolioBuilding {
  id: string;
  name: string;
  address: string;
  registry_code?: string | null;
  archived?: boolean | null;
  subscription_plan_activation_pending?: boolean;
}

export interface PortfolioBuildingEdit {
  canEdit: boolean;
  editingId: string | null;
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onCancel: () => void;
  onStart: (building: { id: string; name: string }) => void;
}

export interface PortfolioBuildingArchive {
  showArchived: boolean;
  /** May the viewer act on this view's control — archive, or restore when archived. */
  canArchive: boolean;
  /** Billing's reason for withholding RESTORE, so the control stays and explains itself. */
  billingReason?: string;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
}

/** Projects for one card. `null` = project browsing is off (archived view, RPC missing). */
export interface PortfolioBuildingProjects {
  projects: OrgProjectOverview[];
  isLoading: boolean;
  matchedProjectIds: Set<number>;
  defaultExpanded: boolean;
  showActivity: boolean;
  onAddProject?: () => void;
}

interface PortfolioBuildingCardProps {
  building: PortfolioBuilding;
  /** Where the building's name leads — the same destination as a table row click. */
  entryPath: string;
  edit: PortfolioBuildingEdit;
  archive: PortfolioBuildingArchive;
  projects: PortfolioBuildingProjects | null;
}

function BuildingRenameForm({ edit }: { edit: PortfolioBuildingEdit }) {
  const { t } = useLanguage();
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <Input
        value={edit.value}
        onChange={(event) => edit.onChange(event.target.value)}
        onKeyDown={edit.onKeyDown}
        aria-label={t.buildings.name}
        className="h-8 max-w-lg"
      />
      <Button size="icon" className="h-8 w-8" aria-label={t.common.save} onClick={edit.onSave}>
        <Save className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        aria-label={t.common.cancel}
        onClick={edit.onCancel}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

/** Archive / restore confirmation, worded for the view it is in. */
function ArchiveConfirmation(props: {
  building: PortfolioBuilding;
  archive: PortfolioBuildingArchive;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { building, archive, open, onOpenChange } = props;
  const { t } = useLanguage();
  const restoring = archive.showArchived;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent onCloseAutoFocus={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {restoring ? t.buildings.restoreTitle : t.buildings.archiveTitle}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {restoring ? t.buildings.restoreConfirm : t.buildings.archiveConfirm}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t.common.cancel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() =>
              restoring ? archive.onRestore(building.id) : archive.onArchive(building.id)
            }
            className={
              restoring
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-destructive text-destructive-foreground hover:bg-destructive/90"
            }
          >
            {restoring ? t.buildings.restore : t.buildings.archive}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Rename and archive behind one "…" menu; restore stays a labelled button. */
function BuildingAdministration(props: {
  building: PortfolioBuilding;
  edit: PortfolioBuildingEdit;
  archive: PortfolioBuildingArchive;
}) {
  const { building, edit, archive } = props;
  const { t } = useLanguage();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const canAct = archive.canArchive || !!archive.billingReason;
  const confirm = (
    <ArchiveConfirmation {...props} open={confirmOpen} onOpenChange={setConfirmOpen} />
  );
  if (archive.showArchived) {
    if (!canAct) return null;
    return (
      <>
        <PermissionAwareButton
          variant="outline"
          size="sm"
          className="h-8"
          onClick={() => setConfirmOpen(true)}
          requiredPermission="canRestoreBuildings"
          permissionMessage={archive.billingReason ?? t.common.permissions.restoreBuildings}
        >
          <ArchiveRestore className="mr-1.5 h-4 w-4" />
          {t.buildings.restore}
        </PermissionAwareButton>
        {confirm}
      </>
    );
  }
  if (!edit.canEdit && !archive.canArchive) return null;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`${t.buildings.list.buildingActions} · ${building.name}`}
            className="h-8 w-8 text-muted-foreground"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {edit.canEdit && (
            <DropdownMenuItem onSelect={() => edit.onStart(building)}>
              <Pencil className="mr-2 h-4 w-4" />
              {t.common.edit}
            </DropdownMenuItem>
          )}
          {archive.canArchive && (
            <DropdownMenuItem
              onSelect={() => setConfirmOpen(true)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t.buildings.archive}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {confirm}
    </>
  );
}

/**
 * One building on the All buildings page, in the building dashboard's project
 * tile look (`projectTile`: white surface, hairline, soft lift, a grey icon and a
 * 14px semibold name): identity and
 * administration on top, its projects below behind a remembered disclosure.
 */
export function PortfolioBuildingCard(props: PortfolioBuildingCardProps) {
  const { building, entryPath, edit, archive, projects } = props;
  const { t } = useLanguage();
  const activationPending = building.subscription_plan_activation_pending === true;
  const editing = edit.canEdit && edit.editingId === building.id;
  const details = [formatBuildingAddress(building.address), building.registry_code]
    .filter(Boolean)
    .join(" · ");
  return (
    <article
      className={cn("min-w-0 rounded-[12px] p-3 sm:p-4", PROJECT_TILE_SURFACE_CLASS)}
      data-building-card={building.id}
      aria-label={building.name}
    >
      <div className="flex items-start gap-3">
        <BuildingIcon className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-1">
          {editing ? (
            <BuildingRenameForm edit={edit} />
          ) : (
            <h2 className="min-w-0 text-[14px] font-semibold leading-snug [overflow-wrap:anywhere]">
              {activationPending ? (
                building.name
              ) : (
                <Link
                  to={entryPath}
                  className="rounded hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {building.name}
                </Link>
              )}
            </h2>
          )}
          {details && (
            <p className="min-w-0 text-xs text-muted-foreground [overflow-wrap:anywhere]">
              {details}
            </p>
          )}
          {activationPending && (
            <p role="status" className="w-full text-xs text-muted-foreground">
              {t.billing.plans.activationPending}
            </p>
          )}
        </div>
        {!editing && (
          <div className="ml-auto shrink-0">
            <BuildingAdministration building={building} edit={edit} archive={archive} />
          </div>
        )}
      </div>
      {projects && !activationPending && (
        <div className="mt-3">
          <PortfolioProjectRows
            buildingId={building.id}
            projects={projects.projects}
            isLoading={projects.isLoading}
            highlightedProjectIds={projects.matchedProjectIds}
            defaultExpanded={projects.defaultExpanded}
            showActivity={projects.showActivity}
            onAddProject={projects.onAddProject}
          />
        </div>
      )}
    </article>
  );
}
